import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((res) => mounted && setUser(res.data))
      .catch(() => mounted && setUser(false))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Server sets httpOnly cookies; we keep the user object only.
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, brand_name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, brand_name });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    // Best-effort server logout: cookies must always be cleared client-side
    // even if the server call fails (offline, 5xx, etc.) — otherwise we'd
    // leave the user stuck in an "authed" state in the UI.
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Logout API call failed (clearing local state anyway):", err?.message || err);
    }
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch { return null; }
  };

  const upgrade = async (origin_url, plan_id = "annual_100") => {
    try {
      const { data } = await api.post("/account/upgrade", { origin_url, plan_id });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const pollUpgradeStatus = async (session_id) => {
    try {
      const { data } = await api.get(`/account/upgrade/status/${session_id}`);
      if (data.is_paid) {
        setUser((u) => (u ? { ...u, is_paid: true, plan: "annual_100" } : u));
      }
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setUser, refresh, upgrade, pollUpgradeStatus }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
